import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { assetService } from "../../api/assetService";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Tabs from "../../components/ui/Tabs";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import { ASSET_TABS, MY_ASSETS_KEY, MY_CLEARANCE_KEY } from "./assetUtils";
import { useMyCurrentAssets } from "./usePendingAcknowledgements";
import AssetCard from "./AssetCard";
import AssetHistoryTable from "./AssetHistoryTable";
import ClearanceCard from "./ClearanceCard";
import ui from "../../components/ui/ui.module.css";
import styles from "./MyAssets.module.css";

const MyAssetsView = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("current");
  const [ackTarget, setAckTarget] = useState(null);

  const currentQuery = useMyCurrentAssets();
  const assignments = currentQuery.data?.data ?? [];
  const pendingCount = currentQuery.data?.pendingAcknowledgement ?? 0;

  const acknowledgeMutation = useMutation({
    mutationFn: (id) => assetService.acknowledge(id),
    onSuccess: () => toast.success("Thanks! Receipt confirmed."),
    onError: (error) =>
      toast.error(getErrorMessage(error, "Unable to confirm receipt.")),
    onSettled: () => {
      setAckTarget(null);
      queryClient.invalidateQueries({ queryKey: [MY_ASSETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_CLEARANCE_KEY] });
    },
  });

  if (!getEmployeeId()) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        Your account is not linked to an employee record.
      </div>
    );
  }

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h4 className="page-title">My Assets</h4>
          <p className="page-subtitle">Company assets assigned to you.</p>
        </div>
      </div>

      {pendingCount > 0 && (
        <div className={cx(ui.alert, ui.alertWarning)}>
          <span>
            <i className="bi bi-box-seam me-2"></i>
            You have <strong>{pendingCount}</strong> asset(s) to acknowledge.
            Confirm each one you've received.
          </span>
        </div>
      )}

      <ClearanceCard />

      <div className={ui.card}>
        <Tabs tabs={ASSET_TABS} active={tab} onChange={setTab} />

        {tab === "history" ? (
          <AssetHistoryTable />
        ) : currentQuery.isLoading ? (
          <div className={ui.empty}>Loading assets…</div>
        ) : currentQuery.isError ? (
          <div className={cx(ui.alert, ui.alertDanger, "m-3")}>
            {getErrorMessage(currentQuery.error, "Unable to load your assets.")}
          </div>
        ) : assignments.length === 0 ? (
          <div className={ui.empty}>
            <i className="bi bi-box fs-3"></i>
            No company assets are assigned to you.
          </div>
        ) : (
          <div className={styles.assetGrid}>
            {assignments.map((assignment) => (
              <AssetCard
                key={assignment.id}
                assignment={assignment}
                onAcknowledge={setAckTarget}
              />
            ))}
          </div>
        )}
      </div>

      {ackTarget && (
        <ConfirmDialog
          title="Confirm receipt"
          message={`Confirm that you received ${ackTarget.asset.name} (${ackTarget.asset.assetTag}) in ${ackTarget.conditionOut.toLowerCase()} condition?`}
          confirmLabel="I received this"
          isPending={acknowledgeMutation.isPending}
          onConfirm={() => acknowledgeMutation.mutate(ackTarget.id)}
          onClose={() => setAckTarget(null)}
        />
      )}
    </div>
  );
};

export default MyAssetsView;
